import type { AssignmentMedia } from '../../model/assignments/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'

interface AssignmentTemplateMediaLinkApiOptions {
  templateId: string | number
  /** Object path (`assignment_templates/{id}/{uuid}.{ext}`), never a URL. */
  path: string
  onError?: (message: string) => void
}

/**
 * `GET …/media?path=…` — re-sign one object. `{template_id}` is the authorization subject only:
 * lookup is guarded by the `assignment_templates/` prefix, so a duplicate's inherited paths (which
 * carry the original's id) resolve too. A 404 is not retryable — re-upload, not refresh. Silent by
 * design: it runs whenever an image comes into view; the caller renders an inline note.
 */
export const useAssignmentTemplateMediaLinkApi = async (
  options: AssignmentTemplateMediaLinkApiOptions,
): Promise<AssignmentMedia | null> => {
  const { $axios } = useNuxtApp()

  const url = useAdminRoutes('admin.assignment-templates.media.show', {
    template_id: options.templateId,
    path: options.path,
  })

  if (!url) {
    options.onError?.('Unexpected error. Invalid request URL.')
    return null
  }

  try {
    const { data } = await $axios.get<ApiResponse>(url)

    if (!data.success) {
      options.onError?.(data.message)
      return null
    }

    const media: AssignmentMedia | null = data.data?.media ?? null

    // A `200` with no link is a broken contract.
    if (!media?.url) {
      options.onError?.('The image could not be loaded.')
      return null
    }

    return media
  } catch (err: unknown) {
    options.onError?.(getAxiosErrorMessage(err))
    return null
  }
}
